import { useEffect, useState, useContext, useCallback, useRef } from "react";
import api, { getEmpresa, updateEmpresa, uploadLogoEmpresa } from "../lib/api";
import { AuthContext } from "../App";
import {
  Button, Input, Select, FormField, Dialog,
  Badge, Spinner, PageHeader,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
  Card, CardContent, CardHeader, CardTitle,
} from "../components/ui";
import { Plus, Sun, Moon, Zap, Eye, EyeOff, Upload, Flame, Gem, Leaf } from "lucide-react";
import { toast } from "sonner";

const TABS = ["Empresa", "Usuarios", "Preferencias"];
const ROLES = ["ADMIN", "RECEPCION", "TECNICO", "CONTADOR"];
const ROLE_COLOR = {
  ADMIN:     "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300",
  RECEPCION: "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300",
  TECNICO:   "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300",
  CONTADOR:  "bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-900/30 dark:text-violet-300",
};

// ─── Tab: Empresa ─────────────────────────────────────────────────────────────
function TabEmpresa() {
  const [form, setForm] = useState({
    nombre: "", label: "", ruc: "", telefono: "", direccion: "", email: "",
  });
  const [logoUrl, setLogoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const { user } = useContext(AuthContext);
  const isAdmin = user?.rol === "ADMIN";

  const load = useCallback(async () => {
    try {
      const r = await getEmpresa();
      const d = r.data;
      setForm({
        nombre:    d.nombre    ?? "",
        label:     d.label     ?? "",
        ruc:       d.ruc       ?? "",
        telefono:  d.telefono  ?? "",
        direccion: d.direccion ?? "",
        email:     d.email     ?? "",
      });
      setLogoUrl(d.logo_url ?? null);
    } catch { toast.error("No se pudo cargar datos de la empresa"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const s = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await updateEmpresa(form);
      toast.success("Datos guardados");
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  }

  async function handleLogo(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const r = await uploadLogoEmpresa(file);
      setLogoUrl(r.data.logo_url);
      toast.success("Logo actualizado");
    } catch { toast.error("Error al subir el logo"); }
    finally { setUploading(false); }
  }

  const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, "");

  if (loading) return <Spinner />;

  return (
    <div className="space-y-5 max-w-xl">
      {/* Logo */}
      <Card>
        <CardHeader><CardTitle>Logo de la empresa</CardTitle></CardHeader>
        <CardContent className="flex items-center gap-5">
          <div className="w-28 h-28 rounded-lg border-2 border-dashed border-border flex items-center justify-center bg-secondary/40 overflow-hidden flex-shrink-0">
            {logoUrl
              ? <img src={`${API_BASE}${logoUrl}`} alt="Logo" className="object-contain w-full h-full p-1" />
              : <span className="text-xs text-muted-foreground text-center px-2">Sin logo</span>
            }
          </div>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Se usará en presupuestos y hoja de conformidad.</p>
            <p className="text-xs text-muted-foreground">PNG, JPG, SVG — máx 12 MB</p>
            <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp,.svg" className="hidden" onChange={handleLogo} />
            <Button
              type="button"
              variant="outline"
              loading={uploading}
              disabled={!isAdmin}
              onClick={() => fileRef.current?.click()}
            >
              <Upload className="w-4 h-4" />
              {logoUrl ? "Cambiar logo" : "Subir logo"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Datos */}
      <Card>
        <CardHeader><CardTitle>Datos de la empresa</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={save} className="space-y-3">
            <FormField label="Nombre *">
              <Input value={form.nombre} onChange={(e) => s("nombre", e.target.value)} required disabled={!isAdmin} />
            </FormField>
            <FormField label="Label / Descripción">
              <Input value={form.label} onChange={(e) => s("label", e.target.value)} placeholder="Taller de reparación…" disabled={!isAdmin} />
            </FormField>
            <FormField label="RUC">
              <Input value={form.ruc} onChange={(e) => s("ruc", e.target.value)} placeholder="80000000-0" disabled={!isAdmin} />
            </FormField>
            <FormField label="Dirección">
              <Input value={form.direccion} onChange={(e) => s("direccion", e.target.value)} disabled={!isAdmin} />
            </FormField>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormField label="Teléfono / Cel">
                <Input value={form.telefono} onChange={(e) => s("telefono", e.target.value)} disabled={!isAdmin} />
              </FormField>
              <FormField label="Email">
                <Input type="email" value={form.email} onChange={(e) => s("email", e.target.value)} disabled={!isAdmin} />
              </FormField>
            </div>
            {isAdmin && (
              <Button type="submit" loading={saving} className="mt-2">Guardar cambios</Button>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Tab: Usuarios ────────────────────────────────────────────────────────────
function TabUsuarios() {
  const { user } = useContext(AuthContext);
  const [users,  setUsers]  = useState([]);
  const [loading,setLoading]= useState(true);
  const [open,   setOpen]   = useState(false);
  const [editing,setEditing]= useState(null);
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({ nombre: "", email: "", password: "", rol: "RECEPCION", activo: true });

  const load = useCallback(async () => {
    try {
      const r = await api.get("/dashboard/usuarios");
      setUsers(r.data);
    } catch { toast.error("Sin permisos para ver usuarios"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  function openCreate() {
    setEditing(null);
    setForm({ nombre: "", email: "", password: "", rol: "RECEPCION", activo: true });
    setOpen(true);
  }

  function openEdit(u) {
    setEditing(u);
    setForm({ nombre: u.nombre, email: u.email, password: "", rol: u.rol, activo: u.activo });
    setOpen(true);
  }

  const s = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { nombre: form.nombre, email: form.email, rol: form.rol, activo: form.activo };
      if (form.password) payload.password = form.password;
      if (editing) {
        await api.put(`/dashboard/usuarios/${editing.id}`, payload);
        toast.success("Usuario actualizado");
      } else {
        if (!form.password) { toast.error("La contraseña es obligatoria"); setSaving(false); return; }
        await api.post("/dashboard/usuarios", { ...payload, password: form.password });
        toast.success("Usuario creado");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error");
    } finally { setSaving(false); }
  }

  const isAdmin = user?.rol === "ADMIN";

  if (loading) return <Spinner />;
  if (!isAdmin) return (
    <Card className="max-w-md">
      <CardContent className="p-6 text-center text-muted-foreground">Solo el Administrador puede gestionar usuarios.</CardContent>
    </Card>
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">{users.length} usuarios registrados</p>
        <Button onClick={openCreate}><Plus className="w-4 h-4" /> Nuevo usuario</Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nombre</TableHead><TableHead>Email</TableHead>
            <TableHead>Rol</TableHead><TableHead>Estado</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((u) => (
            <TableRow key={u.id}>
              <TableCell className="font-medium">{u.nombre}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
              <TableCell><Badge className={ROLE_COLOR[u.rol]}>{u.rol}</Badge></TableCell>
              <TableCell>
                <Badge className={u.activo
                  ? "bg-green-100 text-green-700 border-green-200"
                  : "bg-gray-100 text-gray-500 border-gray-200"
                }>{u.activo ? "Activo" : "Inactivo"}</Badge>
              </TableCell>
              <TableCell>
                <button onClick={() => openEdit(u)} className="text-sm text-primary hover:underline">Editar</button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} title={editing ? "Editar usuario" : "Nuevo usuario"} size="sm">
        <form onSubmit={handleSubmit} className="space-y-3">
          <FormField label="Nombre *">
            <Input value={form.nombre} onChange={(e) => s("nombre", e.target.value)} required />
          </FormField>
          <FormField label="Email *">
            <Input type="email" value={form.email} onChange={(e) => s("email", e.target.value)} required />
          </FormField>
          <FormField label={editing ? "Nueva contraseña (dejar vacío = no cambiar)" : "Contraseña *"}>
            <div className="relative">
              <Input
                type={showPw ? "text" : "password"}
                value={form.password}
                onChange={(e) => s("password", e.target.value)}
                required={!editing}
                className="pr-9"
              />
              <button type="button" onClick={() => setShowPw((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </FormField>
          <FormField label="Rol">
            <Select value={form.rol} onChange={(e) => s("rol", e.target.value)}>
              {ROLES.map((r) => <option key={r}>{r}</option>)}
            </Select>
          </FormField>
          {editing && (
            <FormField label="Estado">
              <Select value={form.activo ? "1" : "0"} onChange={(e) => s("activo", e.target.value === "1")}>
                <option value="1">Activo</option>
                <option value="0">Inactivo</option>
              </Select>
            </FormField>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button type="submit" loading={saving}>{editing ? "Guardar" : "Crear"}</Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}

// ─── Tab: Preferencias ────────────────────────────────────────────────────────
function TabPreferencias() {
  const { theme, setTheme } = useContext(AuthContext);

  const themes = [
    { id: "light",     label: "Claro",     icon: <Sun className="w-5 h-5" />,   desc: "Fondo blanco, contraste alto" },
    { id: "dark",      label: "Oscuro",    icon: <Moon className="w-5 h-5" />,  desc: "Fondo carbón, luce de noche" },
    { id: "ether",     label: "Éter",      icon: <Zap className="w-5 h-5" />,   desc: "Neón sobre negro profundo" },
    { id: "rojo",      label: "Rojo",      icon: <Flame className="w-5 h-5" />, desc: "Fondo blanco, acento carmesí" },
    { id: "violeta",   label: "Violeta",   icon: <Gem className="w-5 h-5" />,   desc: "Oscuro con acentos violeta" },
    { id: "esmeralda", label: "Esmeralda", icon: <Leaf className="w-5 h-5" />,  desc: "Oscuro con acentos verdes" },
  ];

  return (
    <div className="space-y-6 max-w-xl">
      <Card>
        <CardHeader><CardTitle>Tema visual</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-xl">
          {themes.map((t) => (
            <button
              key={t.id}
              onClick={() => setTheme(t.id)}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                theme === t.id
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/40 hover:bg-secondary"
              }`}
            >
              <div className={`mb-2 ${theme === t.id ? "text-primary" : "text-muted-foreground"}`}>
                {t.icon}
              </div>
              <p className="font-semibold text-sm">{t.label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{t.desc}</p>
            </button>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Numeración de documentos</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { label: "Recepciones",  prefix: "REC" },
            { label: "Presupuestos", prefix: "PRE" },
            { label: "Órdenes",      prefix: "OT"  },
            { label: "Facturas",     prefix: "FAC" },
          ].map((d) => (
            <div key={d.prefix} className="flex items-center justify-between p-3 rounded-md bg-secondary/50">
              <span className="text-sm text-muted-foreground">{d.label}</span>
              <code className="font-mono text-sm font-semibold">{d.prefix}-XXXXX</code>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Sistema() {
  const [tab, setTab] = useState(0);

  return (
    <div className="p-4 sm:p-6">
      <PageHeader title="Sistema" sub="Configuración y administración" />
      <div className="flex overflow-x-auto border-b border-border mb-5">
        {TABS.map((t, i) => (
          <button
            key={t}
            onClick={() => setTab(i)}
            className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              tab === i
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t}
          </button>
        ))}
      </div>
      {tab === 0 && <TabEmpresa />}
      {tab === 1 && <TabUsuarios />}
      {tab === 2 && <TabPreferencias />}
    </div>
  );
}
